/**
 * KAGRA Budget Platform - Internationalization (i18n) Engine
 * Trilingual dictionary supporting Traditional Chinese (zh-TW), English (en), and Japanese (ja)
 */
import rawI18nDict from './data/i18n_dict.json' with { type: 'json' };

export const SUPPORTED_LOCALES = ['zh-TW', 'en', 'ja'];
export const DEFAULT_LOCALE = 'zh-TW';

export const I18N_DICT = rawI18nDict;

export const CURRENCY_RATES = {
  TWD: { symbol: 'NT$', rate: 1.0, decimals: 0 },
  USD: { symbol: '$', rate: 46000.0 / 1500000.0, decimals: 0 },
  JPY: { symbol: '¥', rate: 7000000.0 / 1500000.0, decimals: 0 }
};

let currentLocale = DEFAULT_LOCALE;

/**
 * Get current active locale
 * @returns {string}
 */
export function getLocale() {
  return currentLocale;
}

/**
 * Set active locale
 * @param {string} locale
 */
export function setLocale(locale) {
  if (SUPPORTED_LOCALES.includes(locale)) {
    currentLocale = locale;
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = locale;
    }
  }
}

/**
 * Retrieve translation string by dot notation path
 * @param {string} path e.g. "system.title" or "calculator.labels.total_budget"
 * @param {string} [locale] optional locale override
 * @param {string} [fallback] optional fallback text
 * @returns {any}
 */
export function t(path, locale = currentLocale, fallback = '') {
  const dict = I18N_DICT[locale] || I18N_DICT[DEFAULT_LOCALE];
  if (!dict) return fallback || path;

  const parts = path.split('.');
  let current = dict;

  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      // Fallback to default locale (zh-TW)
      const fallbackDict = I18N_DICT[DEFAULT_LOCALE];
      let fallbackCurrent = fallbackDict;
      for (const p of parts) {
        if (fallbackCurrent && typeof fallbackCurrent === 'object' && p in fallbackCurrent) {
          fallbackCurrent = fallbackCurrent[p];
        } else {
          return fallback || path;
        }
      }
      return fallbackCurrent;
    }
  }

  return current !== undefined ? current : (fallback || path);
}

/**
 * Format currency amount based on active locale or specified currency
 * @param {number} amountInNTD
 * @param {string} [currency] 'TWD', 'USD', or 'JPY'
 * @param {string} [locale]
 * @returns {string}
 */
export function formatCurrency(amountInNTD, currency = 'TWD', locale = currentLocale) {
  const curInfo = CURRENCY_RATES[currency] || CURRENCY_RATES.TWD;
  const converted = amountInNTD * curInfo.rate;
  
  const formatter = new Intl.NumberFormat(locale === 'zh-TW' ? 'zh-TW' : (locale === 'ja' ? 'ja-JP' : 'en-US'), {
    maximumFractionDigits: curInfo.decimals,
    minimumFractionDigits: 0
  });

  return `${curInfo.symbol} ${formatter.format(Math.round(converted))}`;
}

/**
 * Format standard number with commas
 * @param {number} num
 * @param {number} [decimals=0]
 * @returns {string}
 */
export function formatNumber(num, decimals = 0) {
  if (num === undefined || num === null || isNaN(num)) return '0';
  return Number(num).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

/**
 * Get module metadata by key
 * @param {string} moduleKey e.g. 'tape_archive', 'cephfs_cache', 'osdf_hub', 'compute_pool'
 * @param {string} [locale]
 */
export function getModuleInfo(moduleKey, locale = currentLocale) {
  return t(`modules.${moduleKey}`, locale) || {};
}

/**
 * Get preset metadata by preset key
 * @param {string} presetKey e.g. 'preset_1_flagship_gpu'
 * @param {string} [locale]
 */
export function getPresetInfo(presetKey, locale = currentLocale) {
  return t(`presets.${presetKey}`, locale) || {};
}

/**
 * Get GPU model info translated
 * @param {string} modelKey
 * @param {string} [locale]
 */
export function getGpuInfo(modelKey, locale = currentLocale) {
  return t(`hardware_catalog.gpu_models.${modelKey}`, locale) || {};
}

/**
 * Get CPU model info translated
 * @param {string} cpuKey
 * @param {string} [locale]
 */
export function getCpuInfo(cpuKey, locale = currentLocale) {
  return t(`hardware_catalog.cpu_models.${cpuKey}`, locale) || {};
}
